import { useState } from 'react';
import { z } from 'zod';
import { useLang } from '@/i18n/LanguageContext';
import { ui } from '@/i18n/content/ui';
import { supabase } from '@/integrations/supabase/client';

const emailSchema = z.string().trim().email().max(255);

interface NewsletterFormProps {
  source?: 'footer' | 'page';
  inline?: boolean;
}

type NewsletterInsertClient = {
  from: (table: string) => {
    insert: (values: { email: string; lang: 'nl' | 'en'; source: 'footer' | 'page' }) =>
      PromiseLike<{ error: { code?: string } | null }>;
  };
};

export function NewsletterForm({ source = 'footer', inline = false }: NewsletterFormProps) {
  const lang = useLang();
  const labels = ui[lang];
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(labels.newsletterInvalidEmail);
      setStatus('error');
      return;
    }
    setError(null);
    setStatus('sending');

    try {
      const { error: invokeError } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email: parsed.data, lang, source },
      });

      if (invokeError) {
        // Keep signups working when the optional Brevo sync function has not yet
        // been deployed. The public table only permits inserts, not reads.
        const newsletterClient = supabase as unknown as NewsletterInsertClient;
        const { error: insertError } = await newsletterClient.from('newsletter_subscribers').insert({
          email: parsed.data,
          lang,
          source,
        });

        // A duplicate means this address is already subscribed, which is a
        // successful and privacy-preserving outcome for the visitor.
        if (insertError && insertError.code !== '23505') throw insertError;
      }

      setStatus('success');
      setEmail('');
    } catch (err) {
      console.error('Newsletter subscription failed:', err);
      setStatus('error');
      setError(labels.newsletterError);
    }
  };

  if (status === 'success') {
    return (
      <p className="text-sm text-[var(--viz-accent)]" aria-live="polite">
        {labels.newsletterSuccess}
      </p>
    );
  }

  return (
    <form onSubmit={submit} className={inline ? 'flex flex-wrap flex-col sm:flex-row gap-2' : 'space-y-3'}>
      <div className="flex-1 min-w-[12rem]">
        <label htmlFor={`newsletter-email-${source}`} className="sr-only">
          {labels.email}
        </label>
        <input
          id={`newsletter-email-${source}`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={labels.newsletterPlaceholder}
          maxLength={255}
          required
          className="w-full border border-sitefg/15 rounded-sm px-3 py-2 bg-sitefg/5 focus:outline-none focus:border-[var(--viz-accent)] text-sm"
        />
      </div>
      <button
        type="submit"
        disabled={status === 'sending'}
        className="px-5 py-2 rounded-sm bg-[var(--viz-accent)] text-white text-sm tracking-wide disabled:opacity-60 whitespace-nowrap"
      >
        {status === 'sending' ? labels.newsletterSending : labels.newsletterButton}
      </button>
      {(error || status === 'error') && (
        <p className="w-full text-sm text-red-600" aria-live="assertive">
          {error || labels.newsletterError}
        </p>
      )}
    </form>
  );
}
